import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/civic/enrol')({
  component: EnrolPage,
  head: () => ({
    meta: [
      { title: 'Enrol to vote — Warren-Blackwood Electorate Guide' },
      {
        name: 'description',
        content:
          'Plain-English guide for Warren-Blackwood residents: check your enrolment, update your address after moving, enrol for the first time, provisional voting, the state vs federal rolls, and how preferential voting works.',
      },
      { property: 'og:title', content: 'Enrol to vote — Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          'Check or update enrolment, first-time enrolment, provisional voting, state vs federal rolls, and a plain-English explainer of preferential voting.',
      },
    ],
  }),
});

function EnrolPage() {
  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.5rem',
    color: 'var(--rivergum)',
    marginTop: 'var(--sp-xl)',
    marginBottom: 'var(--sp-sm)',
    borderBottom: '1px solid var(--light-border)',
    paddingBottom: '0.3rem',
    letterSpacing: '0.02em',
  };
  const h3: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.1rem',
    color: 'var(--ink)',
    marginTop: 'var(--sp-md)',
    marginBottom: '0.4rem',
    letterSpacing: '0.02em',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.65,
    marginBottom: 'var(--sp-sm)',
  };
  const link: React.CSSProperties = { color: 'var(--green-mid)', fontWeight: 600 };
  const callout = (bg: string, border: string): React.CSSProperties => ({
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 8,
    padding: 'var(--sp-md)',
    marginBottom: 'var(--sp-md)',
  });

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Civic · Enrol to vote
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        Enrol to vote
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.5,
        }}
      >
        Enrolling and keeping your address up to date is a legal requirement in
        Australia once you turn 18. It only takes a few minutes online. This page
        is a plain-English guide for Warren-Blackwood residents — including what
        to do when you move between towns or onto a rural property.
      </p>

      <div style={callout('#eef5fb', '#b9d4e8')}>
        <strong style={{ color: '#1b4f72' }}>Quick links</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          Federal roll (AEC) —{' '}
          <a href="https://check.aec.gov.au/" style={link}>
            check.aec.gov.au
          </a>{' '}
          · enrol or update at{' '}
          <a href="https://aec.gov.au/enrol" style={link}>
            aec.gov.au/enrol
          </a>
          <br />
          WA state roll (WAEC) —{' '}
          <a href="https://www.elections.wa.gov.au/enrol-vote" style={link}>
            elections.wa.gov.au/enrol-vote
          </a>
          <br />
          AEC info line —{' '}
          <a href="tel:132326" style={link}>13 23 26</a> · WAEC —{' '}
          <a href="tel:136868" style={link}>13 63 06</a>
        </p>
      </div>

      <h2 style={h2}>Check whether you're enrolled</h2>
      <p style={p}>
        If you've enrolled federally at any point and haven't moved, you're
        probably still on the roll — but it's worth a 30-second check.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          Go to{' '}
          <a href="https://check.aec.gov.au/" style={link}>
            check.aec.gov.au
          </a>{' '}
          and enter your name, date of birth and address.
        </li>
        <li>
          The AEC and WAEC share enrolment data, so a federal check confirms
          your state enrolment too in almost every case.
        </li>
        <li>
          If the result says your details don't match, follow the prompt to
          update — you can do it in the same flow.
        </li>
      </ul>

      <h2 style={h2}>Update your address after moving</h2>
      <p style={p}>
        This is the most common one in our part of the world — people moving
        between Manjimup, Bridgetown, Denmark and the small surrounding
        localities, or shifting from town to a rural block. You have{' '}
        <strong>8 weeks</strong> from moving to update your enrolment.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          Update online at{' '}
          <a href="https://aec.gov.au/enrol" style={link}>aec.gov.au/enrol</a>{' '}
          — this updates both your federal and WA state enrolment in one form.
        </li>
        <li>
          You'll need one of: Australian driver licence, passport number, or
          another enrolled person who can confirm your identity.
        </li>
        <li>
          <strong>Rural addresses:</strong> if your property has no street
          number, use the lot number and road name (e.g. "Lot 42 Channybearup
          Road, Pemberton"). The AEC accepts rural and locality addresses —
          enter exactly what's on your rates notice or driver licence.
        </li>
        <li>
          <strong>PO Box only?</strong> You must enrol against the physical
          address where you live, not a PO Box. You can list a postal address
          separately for mail.
        </li>
      </ul>

      <h2 style={h2}>Enrol for the first time</h2>
      <p style={p}>
        You must enrol if you're an Australian citizen aged 18 or over.
        Eligible 16- and 17-year-olds can also{' '}
        <strong>provisionally enrol</strong> so they're automatically activated
        the day they turn 18 — handy if an election lands close to a birthday.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          Apply online at{' '}
          <a href="https://aec.gov.au/enrol" style={link}>aec.gov.au/enrol</a>.
          It usually takes under 5 minutes.
        </li>
        <li>
          You'll need an Australian driver licence, passport, or citizenship
          certificate. Without ID, an existing enrolled voter (family member,
          neighbour) can confirm your identity on the form.
        </li>
        <li>
          New citizens: you can enrol the moment your citizenship is conferred —
          you don't need to wait for the certificate to arrive in the post.
        </li>
      </ul>

      <h2 style={h2}>Provisional voting</h2>
      <p style={p}>
        "Provisional voting" means casting a vote when there's a question about
        your enrolment that can't be resolved at the polling place. Your ballot
        is sealed in an envelope and only counted once your enrolment is
        confirmed afterwards.
      </p>
      <p style={p}>You'll typically be offered a provisional vote if:</p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>Your name isn't on the roll at the polling place you've come to.</li>
        <li>The roll shows you've already voted (a mark next to your name).</li>
        <li>You've recently updated your address and the roll hasn't caught up.</li>
        <li>Your silent-elector details need to be verified.</li>
      </ul>
      <p style={p}>
        Take ID if you have it, and ask staff for a provisional ballot — they're
        trained for it. Don't walk away without voting just because your name
        isn't found.
      </p>

      <h2 style={h2}>State roll vs federal roll</h2>
      <p style={p}>
        In practice there is{' '}
        <strong>one enrolment form and one combined roll</strong>. The AEC
        maintains the national roll and shares the WA portion with the WA
        Electoral Commission under a joint-roll arrangement. Updating once
        updates both.
      </p>
      <p style={p}>What differs is what each election is for:</p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Federal elections</strong> (run by the AEC) — most of
          Warren-Blackwood is in the federal seat of{' '}
          <strong>O'Connor</strong>; the Shire of Augusta-Margaret River is in
          the federal seat of <strong>Forrest</strong>. You also vote for 6 of
          WA's 12 senators. Federal elections happen roughly every 3 years.
        </li>
        <li>
          <strong>WA state elections</strong> (run by the WAEC) — you vote for
          the Legislative Assembly member for{' '}
          <strong>Warren-Blackwood</strong> (currently Bevan) and for the
          Legislative Council. State elections happen every 4 years on a fixed
          date.
        </li>
        <li>
          <strong>Local government elections</strong> (run by your shire, with
          WAEC support) — councillors and sometimes the shire president.
          Held every 2 years in October on a rolling basis.
        </li>
      </ul>

      <h2 style={h2}>Next WA state election</h2>
      <div style={callout('#fff7e6', '#f0d089')}>
        <strong style={{ color: '#7a4f00' }}>Expected: Saturday 10 March 2029</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          The last WA state election was held on 8 March 2025. WA's fixed
          four-year cycle puts the next one in March 2029. The date is set in
          legislation — barring an early dissolution, you can plan around it.
        </p>
      </div>
      <p style={p}>What to expect on the day:</p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          Polling booths open <strong>8am–6pm</strong> on the Saturday across
          schools and halls in Manjimup, Pemberton, Bridgetown, Boyup Brook,
          Nannup, Denmark, Walpole and most smaller localities.
        </li>
        <li>
          You'll receive <strong>two ballot papers</strong>: a small green one
          for the Legislative Assembly (your local seat), and a large white one
          for the Legislative Council (upper house, statewide since the 2021
          reforms).
        </li>
        <li>
          <strong>Early voting</strong> opens roughly 2 weeks before polling
          day at designated centres — useful for shearers, harvest crews,
          tourism operators and anyone working weekends.
        </li>
        <li>
          <strong>Postal voting</strong> is available on application if you
          can't get to a booth — apply through the WAEC once the election is
          called.
        </li>
        <li>
          Voting is compulsory. Failing to vote without a valid reason attracts
          a $20 first-offence penalty, rising to $50.
        </li>
      </ul>

      <h2 style={h2}>Preferential voting — a plain-English explainer</h2>
      <p style={p}>
        Australia doesn't use "first past the post". We use{' '}
        <strong>preferential voting</strong>, which means you number candidates
        in your order of preference rather than picking just one. It's the
        single most-asked question from first-time voters.
      </p>

      <h3 style={h3}>The basic idea</h3>
      <p style={p}>
        Instead of "who do you want to win?", the ballot asks "rank these
        candidates from most preferred to least preferred". If your top choice
        can't win, your vote isn't wasted — it transfers to your next preference.
      </p>

      <h3 style={h3}>How it's counted (Legislative Assembly / House of Reps)</h3>
      <ol style={{ ...p, paddingLeft: '1.4rem' }}>
        <li>Every ballot's <strong>first preferences (1s)</strong> are counted.</li>
        <li>
          If one candidate has more than 50%, they win. Done.
        </li>
        <li>
          If nobody has 50%, the candidate with the <em>fewest</em> votes is
          eliminated. Their ballots are looked at again and reassigned to
          whoever each voter marked as their <strong>2</strong>.
        </li>
        <li>
          Repeat until somebody passes 50%. That candidate is elected.
        </li>
      </ol>
      <p style={p}>
        This is why you'll sometimes hear "preferences decided it" — the
        winner needed second and third preferences from voters whose first
        choice was eliminated.
      </p>

      <h3 style={h3}>How to fill it in</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Green ballot (Legislative Assembly):</strong> number{' '}
          <em>every</em> box from 1 to however many candidates there are. Miss
          a number and your vote is informal (doesn't count).
        </li>
        <li>
          <strong>White ballot (Legislative Council — WA upper house):</strong>{' '}
          since the 2021 reforms WA uses statewide proportional representation.
          You can number at least <strong>1 to 20 below the line</strong>, or
          at least <strong>1 box above the line</strong> (or at least 20 boxes
          below the line) for parties. The
          WAEC's instructions on the ballot are clear — follow them.
        </li>
        <li>
          <strong>Federal House (green):</strong> number every box.{' '}
          <strong>Federal Senate (white):</strong> at least 1–6 above the line
          OR at least 1–12 below the line.
        </li>
      </ul>

      <div style={callout('#eaf3ea', '#bcd9bc')}>
        <strong style={{ color: 'var(--rivergum)' }}>The most important point</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          You can rank candidates in <em>any order you like</em>. Party how-to-vote
          cards are suggestions, not rules. Your preferences are yours — number
          the candidates in the order you actually prefer them, all the way down.
        </p>
      </div>

      <h2 style={h2}>If something's gone wrong</h2>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Didn't get an apparent-failure-to-vote notice but should have?</strong>{' '}
          Contact the AEC on{' '}
          <a href="tel:132326" style={link}>13 23 26</a> or the WAEC on{' '}
          <a href="tel:136868" style={link}>13 63 06</a>.
        </li>
        <li>
          <strong>Silent elector</strong> (address suppressed for safety
          reasons) — apply through the AEC; the form requires a statement
          about why publishing your address would put you or your family at risk.
        </li>
        <li>
          <strong>Overseas or interstate on polling day?</strong> Apply for a
          postal vote, or use an early-voting centre. Overseas voters can also
          vote at Australian embassies and high commissions.
        </li>
        <li>
          <strong>Mobility or disability access</strong> — every WAEC and AEC
          polling place publishes accessibility info. Mobile polling teams
          visit aged-care facilities in our region in the week before
          polling day.
        </li>
      </ul>

      <p style={{ ...p, marginTop: 'var(--sp-xl)', fontSize: '0.9rem', color: 'var(--ink-mid)' }}>
        This page summarises information published by the Australian Electoral
        Commission and the WA Electoral Commission. For binding rules, dates
        and forms, follow the official links above. See also{' '}
        <Link to="/about" style={link}>About this site</Link> and{' '}
        <Link to="/contact" style={link}>Contact Bevan</Link>.
      </p>
    </main>
  );
}
